<template>
  <div
    class="preflop-shortcut prfshrts"
    :class="{ gw_loading: loading }"
  >
    <div class="prfshrts_table-container">
      <PokerTableShortcuts
        :style="`
      --table-hor-height: 18em;
      --table-hor-min-height: 18em;
      --table-hor-min-width: 100%;
      --table-width: 38em;
      --table-ver-max-width: 32em;
      --table-ver-max-height: 42em;
      --clr-hero: var(--clr-hero-default);
      --clr-trainer_player_active: var(--clr-black-3);
      --clr-trainer_player_folded: var(--clr-black-1-2);
      --clr-table-back: var(--clr-black-0-4);
      font-size: 0.875rem;
  `"
        :players="tablePlayers"
        class="text-noselect prfshrts_poker-table"
        @click:player="togglePlayer"
      />
    </div>
    <CheckboxRow
      class="prfshrts_checkbox-row gw_loading_texts"
      style="--clr-row-back: var(--clr-black-1-3)"
      :hasTitle="true"
      :hasAny="false"
      :isSingle="true"
      :hasContent="ch_actions.hasContent"
      :item="ch_actions"
      :loading="loading"
      tstPrefix="preflop_shortcut"
      @update="item => setSelectedActions(item)"
    >
      <template #default="v">
        {{ $t(`shortcut.${v.id}`) }}
      </template>
      <template #after>
        <div
          v-if="!ch_actions.hasContent"
          class="px-2 py-1 clr-yellow b-radius_4 f-center text-medium"
          style="background-color: rgba(255, 184, 0, 0.1)"
          data-tst="no_shortcuts_message"
        >
          <i class="mdi mdi-information-outline mr-2 mb-auto"></i>
          <span v-html="$t(`messages.solution_no_postfop`)"> </span>
        </div>
      </template>
    </CheckboxRow>
    <portal
      to="dialog-actions-buttons"
      class="w-100"
    >
      <v-btn
        id="btn_prefill"
        class="mr-2 ml-auto"
        :disabled="!canAiPrefill"
        :data-tst="`ai_prefill_btn${canAiPrefill ? '' : '_disabled'}`"
        @click.stop="handlePrefillClick"
      >
        <i class="mdi mdi-auto-fix mr-1 text-semi-large"></i>
        <span>
          {{ $t('solfigr.ai_solve') }}
        </span>
      </v-btn>
      <v-btn
        data-tst="btn_shortcut_apply"
        color="primary"
        :disabled="loading"
        @click="$emit('confirm')"
        >{{ $t('btn.confirm') }}</v-btn
      >
    </portal>
  </div>
</template>

<script>
import { getRandomOption } from '@/helpers/helpers'
import {
  getAvailableGames,
  getAvailableShortcuts,
  getShortcutsPositions,
  getShortcutsActionOptions,
  getRandomStandoffShortcut,
} from '@/helpers/practice_trainer'
import PokerTableShortcuts from '@/components/PokerTable/PokerTableShortcuts'
import { getPlayersDefault } from '@/store/gamestate/gamestate'
import CheckboxRow from '@/components/Forms/CheckboxRow'

export default {
  name: 'PreflopShortcut',
  components: {
    PokerTableShortcuts,
    CheckboxRow,
  },
  props: {
    namespace: { required: true },
    gameformat: Object,
    canAISolve: { required: true, type: Boolean },
  },
  emits: ['confirm'],
  data() {
    return {
      l_positions: [],
      l_actions: [],
    }
  },
  created() {
    this.$store.dispatch('api_poker_shortcuts', { namespace: this.namespace, hasDepthList: false })
  },
  methods: {
    setSelectedActions(item) {
      if (JSON.stringify(item.selected) !== JSON.stringify(this.l_actions)) {
        this.l_actions = item.selected
      }
    },
    togglePlayer(player) {
      if (this.l_positions.includes(player.position)) {
        this.l_positions = this.l_positions.filter(p => p !== player.position)
      } else {
        this.l_positions = [player.position, ...this.l_positions]
      }

      this.l_positions = this.l_positions.filter(p => p)
      this.l_positions = [...new Set(this.l_positions)]

      if (this.l_positions.length === 3) {
        this.l_positions = this.l_positions.splice(0, 2)
      }
      if (this.l_positions.length === 1) {
        const rp = getRandomOption({
          options: this.allPokerPositions,
          selected: [this.l_positions[0]],
        })
        this.l_positions = [...this.l_positions, rp]
      }
    },
    setRandomPositions() {
      if (
        !this.available.positions.includes('BTN') &&
        ['SB', 'BB'].every(p => this.available.positions.includes(p))
      ) {
        this.l_positions = ['SB', 'BB']
      } else if (['BTN', 'BB'].every(p => this.available.positions.includes(p))) {
        this.l_positions = ['BTN', 'BB']
      } else {
        this.l_positions = [this.available.positions[0], this.available.positions[1]]
      }
    },
    apply() {
      if (this.labelId) {
        this.$store.dispatch('api_history_shortcut', {
          namespace: this.namespace,
          resetReports: true,
          label_id: this.labelId,
        })
      }
    },
    handlePrefillClick() {
      const tmpNamespace = 'tmp/primary'
      const dialogType = 'solfigr-create-custom-solution-dialog'
      const dialog = `${dialogType}_namespace-${tmpNamespace}_instaPrefill-true_labelId-${this.labelId}`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
  },
  computed: {
    canAiPrefill() {
      return !this.loading && this.canAISolve && this.available.shortcuts.length
    },
    labelId() {
      const params = {
        allShortcuts: this.all.shortcuts,
        start_spot: 'flop',
        actions: this.ch_actions.selected,
        opponents: [this.l_positions[0]],
        heros: [this.l_positions[1]],
      }
      const { standoffShortcut } = getRandomStandoffShortcut(params)
      return standoffShortcut?.label_id
    },
    tablePlayers() {
      const style = { '--clr-player-back-folded': 'var(--clr-black-1)' }
      const hoverStyle = { '--clr-player-primary': 'var(--clr-primary)' }

      const res = this.allPlayers.map(p => {
        const asCheckbox = true

        const { position } = p
        const pos = this.allPokerPositions.includes('BTN') ? 'BTN' : 'SB'
        const is_dealer = position === pos

        const is_folded = this.unavailable.positions.includes(position)
        const checked = !is_folded && this.l_positions.some(plp => plp === position)

        const css_class = is_folded ? 'no-events' : ''

        const r = {
          ...p,
          position,
          asCheckbox,
          is_folded,
          is_active: checked,
          checked,
          is_dealer,
          css_class,
          style: checked ? style : hoverStyle,
        }
        return r
      })
      return res
    },
    ch_actions() {
      const options = this.loading ? [0, 1, 2, 4, 5] : this.all.actions
      const disabled = this.unavailable.actions
      const selected = (
        this.l_actions.length === 1 ? this.l_actions : [this.all.actions[0]]
      ).filter(i => i)

      const res = {
        id: 'actions_for_shortcut_id',
        title: this.$t('shortcut.street_action', { street: this.$t(`common.preflop`) }),
        // title: this.$t('shortcut.street_action', { street: this.$t(`common.flop`) }),
        options,
        selected,
        disabled,
        hasContent: options.length !== disabled.length,
      }
      return res
    },
    all() {
      const apiData = this.$store.getters.api_get_data('api_poker_shortcuts', this.namespace) || []
      const shortcuts = getAvailableShortcuts({
        shortcuts: apiData,
        start_spot: 'flop',
      })
      const positions = getShortcutsPositions({ shortcuts })
      const actions = getShortcutsActionOptions({ shortcuts })
      const res = {
        shortcuts,
        actions,
        positions,
      }
      // console.log('all', res)
      return res
    },
    available() {
      const params = {
        heros: [this.l_positions[0]],
        opponents: [this.l_positions[1]],
        shortcuts: this.all.shortcuts,
        start_spot: 'flop',
      }
      const { shortcuts, actions } = getAvailableGames(params)
      const res = {
        shortcuts,
        actions,
        positions: this.all.positions,
        // actions:this.all.actions,
      }
      // console.log('available', res)
      return res
    },
    unavailable() {
      const positions = this.allPokerPositions.filter(p => !this.available.positions.includes(p))
      const actions = this.all.actions.filter(p => !this.available.actions.includes(p))
      const res = {
        actions,
        positions,
      }
      // console.log('unavailable', res)
      return res
    },
    allPlayers() {
      const gameformat =
        this.gameformat ||
        this.$store.getters[`${this.namespace ? `${this.namespace}/` : ''}gameformat`]
      const res = getPlayersDefault({ gameformat })
      return res
    },
    allPokerPositions() {
      return this.allPlayers.map(p => p.position)
    },
    apiCall() {
      return this.$store.getters.api_get('api_poker_shortcuts', this.namespace)
    },
    apiShortcuts() {
      return this.$store.getters.api_get_data('api_poker_shortcuts', this.namespace) || []
    },
    loading() {
      return this.apiCall?.loading
    },
    // loading() { return !this.apiCall?.loading },
    loaded() {
      return this.apiCall?.loaded
    },
  },
  watch: {
    loaded: {
      immediate: true,
      handler: 'setRandomPositions',
    },
  },
}
</script>

<style lang="scss" scoped>
.prfshrts {
  flex: 1;
  height: 100%;
  width: 36em;
  max-width: calc(100vw - 4em);
  margin: auto;
  padding-bottom: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  &_table-container {
    // flex: 1;
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    // padding: 2em;
    padding: 1em;
    width: 100%;
    // height: 22em;
    max-width: 100vw;
  }
}
@media (max-width: 1366px) {
  .prfshrts {
    align-items: center;
  }
}
@media (max-width: 1023px) {
  .prfshrts {
    &_table-container {
      height: calc(var(--vh-100) * 0.55);
      flex-shrink: 0;
      display: flex;
      width: 100%;
      max-height: 42em;
      max-width: 100vw;
      .prfshrts_poker-table {
        min-height: 100% !important;
      }
    }
  }
}
</style>
